import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    Groups as StudentsListIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintStudentsListPage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const [selectedGroupId, setSelectedGroupId] = useState<number | 'all'>('all');
    const printableRef = useRef<HTMLDivElement>(null);

    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}-${currentYear + 1}`;
    const todayStr = new Date().toISOString().slice(0, 10);

    // Map student to their group
    const studentGroupMap = useMemo(() => {
        const map = new Map<number, string>();
        groupStudents.forEach(gs => {
            if (gs.status !== 'Dropped') {
                const grp = groups.find(g => g.id === gs.group_id);
                if (grp) map.set(gs.student_id, grp.name);
            }
        });
        return map;
    }, [groups, groupStudents]);

    const filteredStudents = useMemo(() => {
        if (selectedGroupId === 'all') {
            return [...students].sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
        }
        const enrolledStudentIds = new Set(
            groupStudents
                .filter(gs => gs.group_id === selectedGroupId && gs.status !== 'Dropped')
                .map(gs => gs.student_id)
        );
        return students
            .filter(s => enrolledStudentIds.has(s.id))
            .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    }, [students, selectedGroupId, groupStudents]);

    const selectedGroupObj = groups.find(g => g.id === selectedGroupId);
    const groupLabel = selectedGroupId === 'all'
        ? (language === 'ar' ? 'جميع الأقسام' : language === 'en' ? 'All Classes' : 'Toutes les classes')
        : (selectedGroupObj?.name || '');

    const docTitle = language === 'ar'
        ? `قائمة التلاميذ — ${groupLabel}`
        : language === 'en'
        ? `Students List — ${groupLabel}`
        : `Liste des apprenants — ${groupLabel}`;

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '5px 7px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    // Export to Excel
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الرقم' : language === 'en' ? 'Matricule' : 'N° Matricule',
            language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom',
            language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Birth Date' : 'Né(e) le',
            language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe',
            language === 'ar' ? 'الولي' : language === 'en' ? 'Parent' : 'Parent',
            language === 'ar' ? 'الهاتف' : language === 'en' ? 'Phone' : 'Téléphone'
        ];

        const rows = filteredStudents.map(s => {
            const grp = studentGroupMap.get(s.id) || s.school_level || (language === 'ar' ? 'بدون قسم' : 'Sans classe');
            const parentName = s.guardian_first_name ? `${s.guardian_first_name} ${s.guardian_last_name || ''}`.trim() : (s.emergency_contact || '—');
            const parentPhone = s.guardian_phone || s.phone || '—';
            return [
                s.code || s.STD_CODE || s.id,
                `${s.last_name || ''} ${s.first_name || ''}`.trim(),
                s.date_of_birth || '—',
                grp,
                parentName,
                parentPhone
            ];
        });

        exportToExcel(`Liste_Eleves_${groupLabel.replace(/\s+/g, '_')}`, 'Eleves', headers, rows);
    };

    // Export to Word
    const handleExportWord = () => {
        if (!printableRef.current) return;
        exportToWord(`Liste_Eleves_${groupLabel.replace(/\s+/g, '_')}`, docTitle, printableRef.current.innerHTML, isRtl);
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                    value={selectedGroupId}
                    onChange={e => setSelectedGroupId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                >
                    <MenuItem value="all">
                        {language === 'ar' ? 'جميع الأقسام' : language === 'en' ? 'All Classes' : 'Toutes les classes'}
                    </MenuItem>
                    {groups.map(g => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'قائمة التلاميذ' : language === 'en' ? 'Students List' : 'Liste des apprenants'}
            icon={<StudentsListIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'قائمة التلاميذ' : language === 'en' ? 'STUDENTS LIST' : 'LISTE DES APPRENANTS'}
                    subtitle={`${language === 'ar' ? 'السنة الدراسية' : language === 'en' ? 'Academic Year' : 'Année scolaire'} ${academicYear}`}
                    showBorderBottom
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, fontWeight: 600 }}>
                    <div>
                        {filteredStudents.length} {language === 'ar' ? 'تلميذ' : language === 'en' ? 'student(s)' : 'apprenant(s)'}
                    </div>
                    <div>
                        {todayStr}
                    </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                            <th style={cellStyle(90, { textAlign: 'center' })}>
                                {language === 'ar' ? 'الرقم' : language === 'en' ? 'Matricule' : 'N° MATRICULE'}
                            </th>
                            <th style={cellStyle()}>
                                {language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'NOM ET PRÉNOM'}
                            </th>
                            <th style={cellStyle(95)}>
                                {language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Birth Date' : 'NÉ(E) LE'}
                            </th>
                            <th style={cellStyle(110)}>
                                {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'CLASSE'}
                            </th>
                            <th style={cellStyle(140)}>
                                {language === 'ar' ? 'الولي' : language === 'en' ? 'Parent' : 'PARENT'}
                            </th>
                            <th style={cellStyle(120)}>
                                {language === 'ar' ? 'الهاتف' : language === 'en' ? 'Phone' : 'TÉLÉPHONE'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudents.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ ...cellStyle(), textAlign: 'center', padding: '20px 10px', color: '#777' }}>
                                    {language === 'ar' ? 'لا يوجد تلاميذ' : language === 'en' ? 'No students found' : 'Aucun apprenant trouvé'}
                                </td>
                            </tr>
                        ) : (
                            filteredStudents.map(s => {
                                const grp = studentGroupMap.get(s.id) || s.school_level || (language === 'ar' ? 'بدون قسم' : 'Sans classe');
                                const parentName = s.guardian_first_name ? `${s.guardian_first_name} ${s.guardian_last_name || ''}`.trim() : (s.emergency_contact || '—');
                                const parentPhone = s.guardian_phone || s.phone || '—';
                                return (
                                    <tr key={s.id}>
                                        <td style={cellStyle(90, { textAlign: 'center', fontWeight: 'bold' })}>
                                            {s.code || s.STD_CODE || s.id}
                                        </td>
                                        <td style={cellStyle(undefined, { fontWeight: 600 })}>
                                            {s.last_name} {s.first_name}
                                        </td>
                                        <td style={cellStyle(95)}>{s.date_of_birth || '—'}</td>
                                        <td style={cellStyle(110)}>{grp}</td>
                                        <td style={cellStyle(140)}>{parentName}</td>
                                        <td style={cellStyle(120)}>{parentPhone}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {/* Footer notes & signatures */}
                <div style={{ marginTop: 14, fontSize: 11, color: '#555' }}>
                    {filteredStudents.length} {language === 'ar' ? 'سطر — شكراً على ثقتكم.' : language === 'en' ? 'line(s) — Thank you for your trust.' : 'ligne(s) — Merci de votre confiance.'}
                </div>

                <table style={{ width: '100%', marginTop: 35, borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left' }}>
                                <strong>{language === 'ar' ? 'المدير :' : language === 'en' ? 'Director :' : 'Le directeur :'}</strong>
                                <div style={{ height: 60 }} />
                                <div style={{ borderTop: '1px solid #999', width: '60%' }} />
                            </td>
                            <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right' }}>
                                <strong>{language === 'ar' ? 'التأشيرة :' : language === 'en' ? 'Visa :' : 'Visa :'}</strong>
                                <div style={{ height: 60 }} />
                                <div style={{ borderTop: '1px solid #999', width: '60%', marginInlineStart: 'auto' }} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </GenericPage>
    );
};

export default PrintStudentsListPage;
